/**
 * Canonical list of place services (amenities) editable in the dashboard.
 * Keys match the fields of place.dine rendered on the visitor place detail
 * page (backend visitor.ssr.service.ts getPlaceServiceItems) — keep the two
 * lists in sync.
 */
export interface PlaceServiceDefinition {
  key: string;
  label: string;
  /** emoji shown on the visitor-facing place details page */
  icon?: string;
}

export const PLACE_SERVICE_DEFINITIONS: PlaceServiceDefinition[] = [
  { key: 'delivery', label: 'Delivery', icon: '🛵' },
  { key: 'dine_in', label: 'Dine-in', icon: '🍽️' },
  { key: 'takeout', label: 'Takeout', icon: '📦' },
  { key: 'curbside_pickup', label: 'Curbside Pickup', icon: '🚗' },
  { key: 'outdoorSeating', label: 'Outdoor Seating', icon: '🌤️' },
  { key: 'liveMusic', label: 'Live Music', icon: '🎵' },
  { key: 'menuForChildren', label: 'Kids Menu', icon: '👶' },
  { key: 'allowsDogs', label: 'Allows Dogs', icon: '🐶' },
  { key: 'goodForChildren', label: 'Good for Children', icon: '🧒' },
  { key: 'goodForGroups', label: 'Good for Groups', icon: '🧑‍🤝‍🧑' },
  { key: 'serves_beer', label: 'Beer', icon: '🍺' },
  { key: 'serves_wine', label: 'Wine', icon: '🍷' },
  { key: 'servesCocktails', label: 'Cocktails', icon: '🍹' },
  { key: 'servesCoffee', label: 'Coffee', icon: '☕' },
  { key: 'servesDessert', label: 'Dessert', icon: '🍨' },
  { key: 'serves_breakfast', label: 'Breakfast', icon: '🥞' },
  { key: 'serves_brunch', label: 'Brunch', icon: '🥑' },
  { key: 'serves_lunch', label: 'Lunch', icon: '🥪' },
  { key: 'serves_dinner', label: 'Dinner', icon: '🍽️' },
  { key: 'restroom', label: 'Restroom', icon: '🚻' },
  { key: 'goodForWatchingSports', label: 'Good for Watching Sports', icon: '⚽' },
];

/**
 * dine holds both snake_case and camelCase variants for these fields
 * (snake_case from the legacy Places API, camelCase from Places API v1).
 * Writes must keep both in sync so either consumer sees the same value.
 */
export const PLACE_SERVICE_TWIN_KEYS: { [key: string]: string } = {
  servesCocktails: 'serves_cocktails',
  servesCoffee: 'serves_coffee',
  servesDessert: 'serves_dessert',
};

/**
 * Keys match the fields of place.accessibilityOptions rendered on the visitor
 * place detail page (backend visitor.ssr.service.ts getPlaceAccessibilityItems).
 */
export const PLACE_ACCESSIBILITY_DEFINITIONS: PlaceServiceDefinition[] = [
  { key: 'wheelchairAccessibleParking', label: 'Accessible Parking', icon: '♿' },
  { key: 'wheelchairAccessibleEntrance', label: 'Accessible Entrance', icon: '♿' },
  { key: 'wheelchairAccessibleRestroom', label: 'Accessible Restroom', icon: '♿' },
  { key: 'wheelchairAccessibleSeating', label: 'Accessible Seating', icon: '♿' },
];

/**
 * Keys match the fields of place.paymentOptions rendered on the visitor
 * place detail page (backend visitor.ssr.service.ts getPlacePaymentOptionItems).
 */
export const PLACE_PAYMENT_DEFINITIONS: PlaceServiceDefinition[] = [
  { key: 'acceptsCashOnly', label: 'Accepts Cash Only', icon: '💵' },
  { key: 'acceptsCreditCards', label: 'Accepts Credit Cards', icon: '💳' },
  { key: 'acceptsDebitCards', label: 'Accepts Debit Cards', icon: '🏧' },
  { key: 'acceptsNfc', label: 'Accepts NFC', icon: '📱' },
];

/**
 * Keys match the fields of place.parkingOptions rendered on the visitor
 * place detail page (backend visitor.ssr.service.ts getPlaceParkingOptionItems).
 */
export const PLACE_PARKING_DEFINITIONS: PlaceServiceDefinition[] = [
  { key: 'freeParkingLot', label: 'Free Parking Lot', icon: '🅿️🛣️' },
  { key: 'freeStreetParking', label: 'Free Street Parking', icon: '🅿️🛣️' },
  { key: 'paidStreetParking', label: 'Paid Street Parking', icon: '🅿️🛣️' },
  { key: 'paidGarageParking', label: 'Paid Garage Parking', icon: '🅿️🏢' },
  { key: 'paidLotParking', label: 'Paid Lot Parking', icon: '🅿️🚗' },
  { key: 'valetParking', label: 'Valet Parking', icon: '🅿️🚗' },
];

export interface PlaceServiceFlags {
  [key: string]: boolean | null | undefined;
}

/** All editable service groups on a place, keyed by their document field. */
export interface PlaceServiceValues {
  dine: PlaceDine;
  accessibilityOptions: PlaceServiceFlags;
  paymentOptions: PlaceServiceFlags;
  parkingOptions: PlaceServiceFlags;
}

export interface PlaceServiceGroup {
  field: keyof PlaceServiceValues;
  /** section title in the dashboard Edit Services modal */
  title: string;
  /** section title on the visitor-facing place details page */
  visitorTitle: string;
  definitions: PlaceServiceDefinition[];
  twinKeys?: { [key: string]: string };
}

export const PLACE_SERVICE_GROUPS: PlaceServiceGroup[] = [
  {
    field: 'dine',
    title: 'Services',
    visitorTitle: 'Experience',
    definitions: PLACE_SERVICE_DEFINITIONS,
    twinKeys: PLACE_SERVICE_TWIN_KEYS,
  },
  {
    field: 'accessibilityOptions',
    title: 'Accessibility',
    visitorTitle: 'Accessibility',
    definitions: PLACE_ACCESSIBILITY_DEFINITIONS,
  },
  {
    field: 'paymentOptions',
    title: 'Payment Options',
    visitorTitle: 'Payment Options',
    definitions: PLACE_PAYMENT_DEFINITIONS,
  },
  {
    field: 'parkingOptions',
    title: 'Parking',
    visitorTitle: 'Parking Options',
    definitions: PLACE_PARKING_DEFINITIONS,
  },
];

export interface PlaceDine {
  [key: string]: boolean | null | undefined;
  delivery?: boolean | null;
  dine_in?: boolean | null;
  serves_beer?: boolean | null;
  serves_breakfast?: boolean | null;
  serves_brunch?: boolean | null;
  serves_dinner?: boolean | null;
  serves_lunch?: boolean | null;
  serves_wine?: boolean | null;
  takeout?: boolean | null;
  curbside_pickup?: boolean | null;
  serves_vegetarian_food?: boolean | null;
  serves_cocktails?: boolean | null;
  serves_dessert?: boolean | null;
  serves_coffee?: boolean | null;
  servesCocktails?: boolean | null;
  servesDessert?: boolean | null;
  servesCoffee?: boolean | null;
  reservable?: boolean | null;
  outdoorSeating?: boolean | null;
  liveMusic?: boolean | null;
  menuForChildren?: boolean | null;
  restroom?: boolean | null;
  goodForChildren?: boolean | null;
  allowsDogs?: boolean | null;
  goodForGroups?: boolean | null;
  goodForWatchingSports?: boolean | null;
}
